'use client';

import React from 'react';
import { motion } from 'framer-motion';
import { ArrowUpIcon, ArrowDownIcon, DotFilledIcon } from '@radix-ui/react-icons';

import { Box, BoxProps } from '../box';
import { Flex } from '../flex';
import { SimpleGrid } from '../simple-grid';
import { Text } from '../text';
import { Icon } from '../icon';
import { Skeleton } from '../skeleton';

// Motion div for animations (using native div to avoid Chakra prop conflicts)
const MotionDiv = motion.div;

export interface QuickStatItem {
  /** Stat label */
  label: string;
  /** Stat value (string or number) */
  value: string | number;
  /** Optional prefix (e.g., currency symbol) */
  prefix?: string;
  /** Optional suffix (e.g., unit) */
  suffix?: string;
  /** Icon to display */
  icon?: React.ReactNode;
  /** Icon color */
  iconColor?: string;
  /** Change/trend indicator */
  change?: {
    value: number;
    type?: 'increase' | 'decrease' | 'neutral';
    label?: string;
  };
  /** Value color override */
  valueColor?: string;
  /** Format value as currency */
  formatAsCurrency?: boolean;
  /** Currency code for formatting */
  currencyCode?: string;
}

export interface DetailQuickStatsProps extends Omit<BoxProps, 'children'> {
  /** Array of stat items to display */
  stats: QuickStatItem[];
  /** Number of columns */
  columns?: number | { base?: number; sm?: number; md?: number; lg?: number; xl?: number };
  /** Card variant */
  variant?: 'default' | 'compact' | 'bordered';
  /** Show dividers between stats */
  showDividers?: boolean;
  /** Loading state */
  isLoading?: boolean;
  /** Animation delay multiplier in seconds */
  staggerDelay?: number;
}

/**
 * DetailQuickStats - Quick stats row for detail pages
 * Features trend indicators, icons, and animated entry
 */
export const DetailQuickStats: React.FC<DetailQuickStatsProps> = ({
  stats,
  columns = { base: 2, md: 4 },
  variant = 'default',
  showDividers = false,
  isLoading = false,
  staggerDelay = 0.1,
  ...boxProps
}) => {
  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: staggerDelay,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 15, scale: 0.95 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.4,
        ease: [0.4, 0, 0.2, 1] as [number, number, number, number],
      },
    },
  };

  // Get change icon and color
  const getChangeIcon = (change?: QuickStatItem['change']) => {
    if (!change) return null;
    const type =
      change.type || (change.value > 0 ? 'increase' : change.value < 0 ? 'decrease' : 'neutral');
    switch (type) {
      case 'increase':
        return ArrowUpIcon;
      case 'decrease':
        return ArrowDownIcon;
      default:
        return DotFilledIcon;
    }
  };

  const getChangeColor = (change?: QuickStatItem['change']) => {
    if (!change) return '#6B7280';
    const type =
      change.type || (change.value > 0 ? 'increase' : change.value < 0 ? 'decrease' : 'neutral');
    switch (type) {
      case 'increase':
        return '#10B981';
      case 'decrease':
        return '#EF4444';
      default:
        return '#6B7280';
    }
  };

  // Format value
  const formatValue = (stat: QuickStatItem): string => {
    if (stat.formatAsCurrency && typeof stat.value === 'number') {
      // Ensure currency code is always a valid string
      const currency = stat.currencyCode && stat.currencyCode.trim() ? stat.currencyCode : 'TRY';
      return new Intl.NumberFormat('tr-TR', {
        style: 'currency',
        currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(stat.value);
    }
    return `${stat.prefix || ''}${stat.value}${stat.suffix || ''}`;
  };

  // Get variant styles
  const getVariantStyles = () => {
    switch (variant) {
      case 'compact':
        return {
          padding: '12px',
        };
      case 'bordered':
        return {
          padding: '16px',
          border: '1px solid var(--chakra-colors-border-muted)',
          borderRadius: '12px',
          backgroundColor: 'var(--chakra-colors-bg-surface)',
        };
      default:
        return {
          padding: '16px',
        };
    }
  };

  const variantStyles = getVariantStyles();

  if (isLoading) {
    return (
      <Box {...boxProps}>
        <SimpleGrid columns={columns} gap={4}>
          {Array.from({ length: 4 }).map((_, index) => (
            <Box
              key={index}
              padding={4}
              backgroundColor="bg.surface"
              borderRadius="lg"
              border="1px solid"
              borderColor="border.muted"
            >
              <Flex gap={3} alignItems="center">
                <Skeleton width="40px" height="40px" borderRadius="lg" />
                <Box flex={1}>
                  <Skeleton width="60px" height="14px" marginBottom={2} />
                  <Skeleton width="100px" height="24px" />
                </Box>
              </Flex>
            </Box>
          ))}
        </SimpleGrid>
      </Box>
    );
  }

  return (
    <MotionDiv variants={containerVariants} initial="hidden" animate="visible">
      <Box {...boxProps}>
        <SimpleGrid columns={columns} gap={4}>
          {stats.map((stat, index) => {
            const ChangeIcon = getChangeIcon(stat.change);
            const changeColor = getChangeColor(stat.change);

            return (
              <MotionDiv key={index} variants={itemVariants}>
                <Box
                  position="relative"
                  backgroundColor="bg.surface"
                  borderRadius="lg"
                  border="1px solid"
                  borderColor="border.muted"
                  overflow="hidden"
                  transition="all 0.2s"
                  _hover={{
                    borderColor: 'border.default',
                    boxShadow: 'sm',
                  }}
                  style={variantStyles}
                >
                  {/* Accent line */}
                  <Box
                    position="absolute"
                    top={0}
                    left={0}
                    right={0}
                    height="3px"
                    background={stat.iconColor || '#3B82F6'}
                    opacity={0.8}
                  />

                  <Flex gap={3} alignItems="center">
                    {/* Icon */}
                    {stat.icon && (
                      <Flex
                        alignItems="center"
                        justifyContent="center"
                        width="44px"
                        height="44px"
                        borderRadius="lg"
                        backgroundColor={`${stat.iconColor || '#3B82F6'}15`}
                        flexShrink={0}
                      >
                        <Icon size="lg" style={{ color: stat.iconColor || '#3B82F6' }}>
                          {stat.icon}
                        </Icon>
                      </Flex>
                    )}

                    {/* Content */}
                    <Box flex={1} minWidth={0}>
                      <Text fontSize="sm" color="fg.muted" marginBottom={1} truncate>
                        {stat.label}
                      </Text>
                      <Flex alignItems="baseline" gap={2}>
                        <Text
                          fontSize="xl"
                          fontWeight="bold"
                          color={stat.valueColor || 'fg.default'}
                          lineHeight="tight"
                          truncate
                        >
                          {formatValue(stat)}
                        </Text>
                        {stat.change && ChangeIcon && (
                          <Flex alignItems="center" gap={0.5}>
                            <Icon size="sm" style={{ color: changeColor }}>
                              <ChangeIcon />
                            </Icon>
                            <Text fontSize="xs" fontWeight="medium" style={{ color: changeColor }}>
                              {Math.abs(stat.change.value)}%
                            </Text>
                          </Flex>
                        )}
                      </Flex>
                      {stat.change?.label && (
                        <Text fontSize="xs" color="fg.muted">
                          {stat.change.label}
                        </Text>
                      )}
                    </Box>
                  </Flex>

                  {/* Divider for showDividers option */}
                  {showDividers && index < stats.length - 1 && (
                    <Box
                      position="absolute"
                      top="20%"
                      right={0}
                      bottom="20%"
                      width="1px"
                      backgroundColor="border.muted"
                      display={{ base: 'none', md: 'block' }}
                    />
                  )}
                </Box>
              </MotionDiv>
            );
          })}
        </SimpleGrid>
      </Box>
    </MotionDiv>
  );
};

export default DetailQuickStats;
